import React, { useState } from 'react';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import cn from 'classnames';
import './create-notetaker-account.scss';
import ControlledTextInput from '../../components/ControlledTextInput';
import ControlledSingleSelectComboBox from '../../components/ControlledSingleSelectComboBox';
import { useForm } from 'react-hook-form';
import { Button } from '@tcl-boron-prefabs/button';
import { CreateNotetakerAccountRequest } from '../../utils/create-account';
import { ContactType } from 'institution-service-shared';
import { emailRegex } from '../../utils/is-valid-email';

const CONTACT_TYPE_OPTIONS = [
  { label: 'Physician', value: ContactType.PHYSICIAN },
  { label: 'Admin', value: ContactType.ADMIN },
  { label: 'Nurse', value: ContactType.NURSE },
  { label: 'Business Leader', value: ContactType.BUSINESS_LEADER },
  { label: 'Other', value: ContactType.OTHER },
];

function CreateNotetakerAccount() {
  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
  } = useForm();

  const onSubmit = async (data) => {
    setSubmitting(true);
    setError(false);
    setSubmitted(false);

    try {
      // only submit form if honeypot fields are empty
      if (
        !data.firstNamehoney &&
        !data.lastNamehoney &&
        !data.emailhoney &&
        !data.contactTypehoney &&
        !data.institutionNamehoney &&
        !data.zipCodehoney &&
        !data.npihoney
      ) {
        await CreateNotetakerAccountRequest(data);
      }

      setSubmitted(true);
    } catch (err) {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <h1 className={cn(typography.H1)} data-testid="submitted-header">
          Check your inbox
        </h1>
        <p className={cn(typography.Paragraph)}>
          An email has been sent to <strong>{getValues('email')}</strong>.
        </p>
        <p className={cn(typography.Paragraph)}>
          If you do not receive an email in 10 minutes, please check your spam folder, then contact{' '}
          <a className={cn(typography.link)} href="mailto:help@tempus.com">
            help@tempus.com{' '}
          </a>
          for assistance accessing your account.
        </p>
      </>
    );
  }

  if (error) {
    return (
      <div className="app-view-container create-account-request-message">
        <h1 className={cn(typography.H1)}>Something went wrong</h1>
        <p className={cn(typography.Paragraph)}>We’re sorry, but we weren’t able to create your Hub account.</p>
        <p className={cn(typography.Paragraph)}>
          Contact{' '}
          <a className={cn(typography.link)} href="mailto:help@tempus.com">
            help@tempus.com{' '}
          </a>
          or call 800-739-4137 for assistance setting up your account.
        </p>
      </div>
    );
  }

  return (
    <div
      className="app-view-container create-account-request-message"
      data-testid="create-notetaker-account-request-form"
    >
      <h1 className={cn(typography.H1)}>Create your AI Notetaker Account</h1>
      <p className={cn(typography.Paragraph)}>
        Start using the AI Notetaker in just a few steps. Fill out the form below to create your account.
      </p>
      <div className="account-form">
        <div className="account-form-row">
          <ControlledTextInput
            control={control}
            name="firstNamehoney"
            label="First Name"
            placeholder="Enter"
            className="account-form-input"
            errors={errors}
            autoComplete={'off'}
            tabIndex={-1}
          />
          <ControlledTextInput
            control={control}
            name="lastNamehoney"
            label="Last Name"
            placeholder="Enter"
            className="account-form-input"
            errors={errors}
            autoComplete={'off'}
            tabIndex={-1}
          />
        </div>
        <div className="account-form-row">
          <ControlledSingleSelectComboBox
            control={control}
            name="contactTypehoney"
            label="Role"
            placeholder="Select"
            options={CONTACT_TYPE_OPTIONS}
            className="account-form-input"
            errors={errors}
            escapeClippingArea
            autoComplete={'off'}
            tabIndex={-1}
          />
          <ControlledTextInput
            control={control}
            name="emailhoney"
            label="Email"
            placeholder="Enter"
            className="account-form-input"
            pattern={{
              value: emailRegex,
              message: 'Please enter a valid email address',
            }}
            errors={errors}
            autoComplete={'off'}
            tabIndex={-1}
          />
        </div>
        <div className="account-form-row">
          <ControlledTextInput
            control={control}
            name="institutionNamehoney"
            label="Practice/Institution Name"
            placeholder="Enter"
            className="account-form-input"
            errors={errors}
            autoComplete={'off'}
            tabIndex={-1}
          />
        </div>
        <div className="account-form-row">
          <ControlledTextInput
            control={control}
            name="zipCodehoney"
            label="Zip Code"
            placeholder="Enter"
            className="account-form-input"
            errors={errors}
            autoComplete={'off'}
            tabIndex={-1}
          />
          <ControlledTextInput
            control={control}
            name="npihoney"
            label="NPI"
            placeholder="Enter"
            className="account-form-input"
            errors={errors}
            autoComplete={'off'}
            tabIndex={-1}
          />
        </div>
      </div>
      <div className="create-account-form">
        <div className="create-account-form-row">
          <ControlledTextInput
            control={control}
            name="firstName"
            label="First Name"
            placeholder="Enter"
            required
            className="create-account-form-input"
            errors={errors}
          />
          <ControlledTextInput
            control={control}
            name="lastName"
            label="Last Name"
            placeholder="Enter"
            required
            className="create-account-form-input"
            errors={errors}
          />
        </div>
        <div className="create-account-form-row">
          <ControlledSingleSelectComboBox
            control={control}
            name="contactType"
            label="Role"
            placeholder="Select"
            options={CONTACT_TYPE_OPTIONS}
            required
            className="create-account-form-input"
            errors={errors}
            escapeClippingArea
          />
          <ControlledTextInput
            control={control}
            name="email"
            label="Email"
            placeholder="Enter"
            required
            className="create-account-form-input"
            pattern={{
              value: emailRegex,
              message: 'Please enter a valid email address',
            }}
            errors={errors}
          />
        </div>
        <div className="create-account-form-row">
          <ControlledTextInput
            control={control}
            name="institutionName"
            label="Practice/Institution Name"
            placeholder="Enter"
            required
            className="create-account-form-input"
            errors={errors}
          />
        </div>
        <div className="create-account-form-row">
          <ControlledTextInput
            control={control}
            name="zipCode"
            label="Zip Code"
            placeholder="Enter"
            required
            className="create-account-form-input"
            errors={errors}
          />
          <ControlledTextInput
            control={control}
            name="npi"
            label="NPI"
            placeholder="Enter"
            className="create-account-form-input"
            errors={errors}
          />
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || submitting}
          loading={submitting}
          data-testid="submit-button"
        >
          Submit
        </Button>
      </div>
    </div>
  );
}

export default CreateNotetakerAccount;
